import Navbar from '../components/Navbar'
import SEO from '../components/SEO'

const SECTIONS = [
  { id: 'hook',      label: 'Hook your reader instantly' },
  { id: 'covers',    label: 'Covers & banners' },
  { id: 'voice',     label: 'Finding your voice' },
  { id: 'pacing',    label: 'Pacing & chapter length' },
  { id: 'dialogue',  label: 'Writing great dialogue' },
  { id: 'show',      label: 'Show, don\'t tell' },
  { id: 'cliffhanger', label: 'Cliffhangers & chapter endings' },
  { id: 'fanfic',    label: 'Fanfic-specific tips' },
  { id: 'feedback',  label: 'Getting & using feedback' },
  { id: 'consistency', label: 'Staying consistent' },
]

export default function WritingTipsPage() {
  return (
    <div className="legal-page">
      <SEO title="Writing Tips" description="Craft advice, story structure guides, and tips to build your audience on Verse." />
      <Navbar />
      <div className="legal-page__inner">
        <header className="legal-page__header">
          <p className="legal-page__label">Writers' Corner</p>
          <h1 className="legal-h1">Writing Tips</h1>
          <p className="legal-page__meta">
            Practical advice to help you write stories readers can't put down.
          </p>
        </header>

        <nav className="legal-toc" aria-label="Table of contents">
          <p className="legal-toc__heading">On this page</p>
          <ol className="legal-toc__list">
            {SECTIONS.map(s => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.label}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ── Hook ── */}
        <section id="hook" className="legal-section">
          <h2 className="legal-h2">Hook your reader instantly</h2>
          <p>
            You have about three sentences before a reader decides whether to keep going.
            Open with something that raises a question, creates tension, or drops them
            straight into action — not backstory. A strong hook doesn't explain; it
            promises something worth staying for.
          </p>
          <ul>
            <li><strong>Start mid-scene.</strong> Skip the weather, the waking up, the mirror descriptions. Drop readers into a moment that already matters.</li>
            <li><strong>Use a compelling voice.</strong> Even a quiet opening can grip readers if the narrator's personality is vivid from word one.</li>
            <li><strong>Raise a question.</strong> "She had exactly nine minutes before the king figured out she wasn't his daughter." Readers need to know what happens next.</li>
            <li><strong>Avoid prologues that summarise.</strong> If your prologue is backstory in disguise, cut it and start at chapter one.</li>
          </ul>
        </section>

        {/* ── Covers & Banners ── */}
        <section id="covers" className="legal-section">
          <h2 className="legal-h2">Covers & banners</h2>
          <p>
            Your cover is the first thing a reader sees — it's your story's handshake.
            On Verse, every story and quiz can have a custom cover image. Use this to
            your advantage.
          </p>
          <ul>
            <li><strong>Pick a mood, not just a character.</strong> A cover that conveys the feeling of your story (dark, romantic, adventurous) will attract the right readers.</li>
            <li><strong>Keep it readable at thumbnail size.</strong> Browse pages show small cards. A cluttered image with tiny text will blend into the background. Bold, high-contrast visuals work best.</li>
            <li><strong>Use your title clearly.</strong> If you add text to your cover, make sure it's legible in both light and dark themes. Dark overlay + white text is a reliable combo.</li>
            <li><strong>Match the cover to the content.</strong> Misleading covers spike your bounce rate — readers click away the moment the story doesn't match what the image promised.</li>
            <li><strong>Recommended size: 1200 × 675 px</strong> (16:9). Verse also supports portrait covers — 800 × 1200 px works great for novel-style stories.</li>
            <li><strong>Story banners</strong> appear at the top of your story page. Use a wide, atmospheric image here — something that sets the scene rather than summarising the plot.</li>
          </ul>
        </section>

        {/* ── Voice ── */}
        <section id="voice" className="legal-section">
          <h2 className="legal-h2">Finding your voice</h2>
          <p>
            Voice is what makes your writing sound like <em>you</em> and no one else.
            It's the hardest thing to teach and the most important thing to develop.
          </p>
          <ul>
            <li><strong>Write like you talk — then edit.</strong> Your natural rhythm is your voice. Over-formal writing often kills it. Draft fast and loose, then polish.</li>
            <li><strong>Read your work aloud.</strong> Awkward sentences, unnatural dialogue, and pacing problems become obvious the second you hear them.</li>
            <li><strong>Read widely.</strong> Writers who only read in their own genre tend to plateau. Read outside your comfort zone and steal techniques liberally.</li>
            <li><strong>Don't imitate your favourite author.</strong> Influence is fine; copying is a dead end. Figure out <em>why</em> you love their writing, then find your own way to achieve the same effect.</li>
          </ul>
        </section>

        {/* ── Pacing ── */}
        <section id="pacing" className="legal-section">
          <h2 className="legal-h2">Pacing & chapter length</h2>
          <p>
            Pacing controls how fast a story feels. Slow scenes build depth; fast scenes
            create excitement. The trick is knowing when to use each.
          </p>
          <ul>
            <li><strong>Short chapters read fast.</strong> If you want urgency — chases, arguments, revelations — keep chapters tight, even under 1,000 words.</li>
            <li><strong>Long chapters build immersion.</strong> World-building, emotional depth, and complex relationships need room to breathe. Don't rush them.</li>
            <li><strong>Vary sentence length deliberately.</strong> Long sentences slow the reader down. Short ones speed up. Alternate them to control tension beat by beat.</li>
            <li><strong>White space is your friend.</strong> On-screen reading is different from print. Short paragraphs and frequent scene breaks reduce fatigue. Wall-of-text chapters drive readers away.</li>
            <li><strong>Cut filler scenes ruthlessly.</strong> Every scene should either advance the plot, reveal character, or both. If it does neither, cut it.</li>
          </ul>
        </section>

        {/* ── Dialogue ── */}
        <section id="dialogue" className="legal-section">
          <h2 className="legal-h2">Writing great dialogue</h2>
          <p>
            Good dialogue does three things at once: reveals character, advances the
            plot, and sounds natural. Most first drafts fail on all three.
          </p>
          <ul>
            <li><strong>People don't speak in full sentences.</strong> Real speech is interrupted, fragmented, and often indirect. Mirror that.</li>
            <li><strong>Each character should sound different.</strong> Vocabulary, rhythm, and what they choose to say (or not say) should be unique to each character.</li>
            <li><strong>Avoid info-dumping through dialogue.</strong> "As you know, Bob, we've been friends since childhood in the village of…" No one talks like this. Find other ways to deliver exposition.</li>
            <li><strong>Action beats &gt; dialogue tags.</strong> Instead of "he said angrily," show the anger: <em>He slammed the mug down. "I'm done."</em></li>
            <li><strong>Subtext is everything.</strong> Characters rarely say exactly what they mean, especially about things that matter. Let tension live in what's unsaid.</li>
          </ul>
        </section>

        {/* ── Show Don't Tell ── */}
        <section id="show" className="legal-section">
          <h2 className="legal-h2">Show, don't tell</h2>
          <p>
            "Show don't tell" is the most repeated writing advice — and the most
            misunderstood. It doesn't mean never state facts. It means don't
            summarise emotions when you can make the reader feel them.
          </p>
          <ul>
            <li><strong>Telling:</strong> <em>She was nervous.</em> &nbsp;|&nbsp; <strong>Showing:</strong> <em>She clicked her pen three times, stopped, clicked it again.</em></li>
            <li><strong>Use the five senses.</strong> Smell and texture are wildly underused. A single specific detail — the burnt-coffee smell of the precinct, the way wet wool clings — is worth a paragraph of description.</li>
            <li><strong>Physical reactions reveal emotions.</strong> Clenched jaw, held breath, a laugh that comes out too loud — the body broadcasts what the mind won't admit.</li>
            <li><strong>Telling has its place.</strong> Rapid plot transitions, time skips, and minor characters can all be told. Save showing for the moments that matter.</li>
          </ul>
        </section>

        {/* ── Cliffhangers ── */}
        <section id="cliffhanger" className="legal-section">
          <h2 className="legal-h2">Cliffhangers & chapter endings</h2>
          <p>
            The end of a chapter is a deal with the reader: "stay with me a little longer."
            Make it worth their while.
          </p>
          <ul>
            <li><strong>End on a question, not an answer.</strong> Resolve one thing; open another. Readers should feel compelled to click "Next chapter."</li>
            <li><strong>Vary your cliffhangers.</strong> Not every chapter needs a dramatic twist. Sometimes the most effective ending is a quiet emotional revelation — a character realising something that changes everything.</li>
            <li><strong>Don't fake it.</strong> Artificial cliffhangers — where the "danger" turns out to be nothing — break trust fast. Make every chapter ending feel earned.</li>
            <li><strong>The last line is prime real estate.</strong> Rewrite it until it's exactly right. That's the sentence that sticks with readers overnight.</li>
          </ul>
        </section>

        {/* ── Fanfic-specific ── */}
        <section id="fanfic" className="legal-section">
          <h2 className="legal-h2">Fanfic-specific tips</h2>
          <p>
            Fanfiction has its own craft challenges. You're writing for readers who
            already love these characters — which is both an advantage and a responsibility.
          </p>
          <ul>
            <li><strong>Respect the canon, then break it intentionally.</strong> The best AUs (alternate universes) understand the source material deeply before they subvert it. Know the rules before you bend them.</li>
            <li><strong>Character voice is sacred.</strong> Readers will forgive a lot — plot holes, slow pacing — but they won't forgive a character who doesn't sound like themselves. Study how they speak and think in canon.</li>
            <li><strong>Tag accurately and generously.</strong> Tags are how readers find you and decide if your story is for them. Under-tagging frustrates; over-tagging is fine.</li>
            <li><strong>Write the story you can't find.</strong> The best fanfic fills a gap — a ship that never got its moment, a side character who deserved more, a "what if" the author never explored. That gap is your unique value.</li>
            <li><strong>Updates matter.</strong> An abandoned WIP (work in progress) is one of the most common complaints in fandom. If life gets in the way, a brief author's note goes a long way. Your readers will wait — just let them know you're still there.</li>
          </ul>
        </section>

        {/* ── Feedback ── */}
        <section id="feedback" className="legal-section">
          <h2 className="legal-h2">Getting & using feedback</h2>
          <p>
            Feedback is how you grow. Learning to ask for it — and actually hear it —
            is one of the most valuable skills a writer can build.
          </p>
          <ul>
            <li><strong>Ask specific questions.</strong> "Does the pacing feel off in chapter 3?" gets you useful answers. "What did you think?" doesn't.</li>
            <li><strong>Separate yourself from the work.</strong> Criticism of your story is not criticism of you. The faster you learn this, the faster you improve.</li>
            <li><strong>Distinguish preference from problem.</strong> "I don't like romance subplots" is preference. "I couldn't tell why the character trusted her so quickly" is a problem worth fixing.</li>
            <li><strong>Leave comments on others' work.</strong> It costs you nothing, and it builds a community that pays you back. Genuine, specific comments ("The reveal in chapter 4 genuinely surprised me") are worth a hundred "great update!!" replies.</li>
          </ul>
        </section>

        {/* ── Consistency ── */}
        <section id="consistency" className="legal-section">
          <h2 className="legal-h2">Staying consistent</h2>
          <p>
            The biggest difference between writers who improve and writers who don't
            isn't talent — it's consistency.
          </p>
          <ul>
            <li><strong>Write on a schedule, not on inspiration.</strong> Inspiration is unreliable. Schedule is not. Even 200 words a day compounds into a finished story.</li>
            <li><strong>Keep a story bible.</strong> Note character descriptions, timeline events, and world-building details somewhere you can reference them. Continuity errors are reader immersion killers.</li>
            <li><strong>Publish before you're ready.</strong> Waiting for perfection is how stories stay unfinished. Good enough and published beats perfect and stuck in your drafts every time.</li>
            <li><strong>Celebrate milestones.</strong> Finishing a chapter, hitting 1,000 reads, getting your first comment — these matter. Acknowledge them. Writing is a long game; find joy in the small wins.</li>
          </ul>
        </section>
      </div>
    </div>
  )
}
